import { Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FindTagPipe } from './find-tag.pipe';
import { RecognitionVisualizationConfig, RecognitionObject } from '../bbox-gallery-card/bbox-gallery-card.component';

// ─────────────────────────────────────────────────────────────────────────────

const TAG_META: Record<string, { label: string; color: string; bg: string }> = {
  postobon:    { label: 'Postobon',    color: '#6366f1', bg: 'rgba(99,102,241,.12)'  },
  competencia: { label: 'Competencia', color: '#f43f5e', bg: 'rgba(244,63,94,.12)'   },
  precios:     { label: 'Precios',     color: '#f59e0b', bg: 'rgba(245,158,11,.12)'  },
  nevera:      { label: 'Nevera',      color: '#14b8a6', bg: 'rgba(20,184,166,.12)'  },
};

interface TagStat {
  key:        string;
  label:      string;
  color:      string;
  bg:         string;
  total:      number;
  named:      number;   // excluye Others
  others:     number;
  avgScore:   number;
  highConf:   number;   // score >= 0.9
  pct:        number;   // % del total de todos los objetos
  barW:       number;   // 0-100 relativo al mayor
}

@Component({
  selector: 'app-recognition-kpi-card',
  standalone: true,
  imports: [CommonModule, FindTagPipe],
  templateUrl: './recognition-kpi-card.component.html',
  styleUrl:    './recognition-kpi-card.component.scss',
})
export class RecognitionKpiCardComponent implements OnChanges {
  @Input() config!: RecognitionVisualizationConfig;
  @Input() loading = false;

  // ── KPIs principales ──────────────────────────────────────────────────────
  ratioLabel       = '—';    // ej. "2.7 : 1"
  ratioSubLabel    = '';     // ej. "Postobon vs Competencia"
  dominanceColor   = '#94a3b8';
  totalObjects     = 0;
  totalImages      = 0;
  avgObjPerImage   = 0;
  othersTotal      = 0;
  othersPct        = 0;

  // ── Stats por tag ─────────────────────────────────────────────────────────
  tagStats: TagStat[] = [];

  ngOnChanges(): void { this.build(); }

  private build(): void {
    if (!this.config?.rows?.length) return;

    this.totalImages = this.config.rows.length;
    const allObjs    = this.config.rows.flatMap(r => r.objects);
    this.totalObjects    = allObjs.length;
    this.avgObjPerImage  = Math.round(this.totalObjects / this.totalImages);
    this.othersTotal     = allObjs.filter(o => o.name === 'Others').length;
    this.othersPct       = Math.round((this.othersTotal / this.totalObjects) * 100);

    // Stats por tag
    const tagKeys = [...new Set(allObjs.map(o => o.tag))].filter(t => TAG_META[t]);
    const stats: TagStat[] = tagKeys.map(tag => {
      const tagObjs  = allObjs.filter(o => o.tag === tag);
      const named    = tagObjs.filter(o => o.name !== 'Others');
      const others   = tagObjs.length - named.length;
      const scores   = tagObjs.map(o => o.score);
      const avgScore = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
      const highConf = scores.filter(s => s >= 0.9).length;
      return {
        key:      tag,
        label:    TAG_META[tag].label,
        color:    TAG_META[tag].color,
        bg:       TAG_META[tag].bg,
        total:    tagObjs.length,
        named:    named.length,
        others,
        avgScore,
        highConf,
        pct:      Math.round((tagObjs.length / this.totalObjects) * 100),
        barW:     0,
      };
    }).sort((a, b) => b.total - a.total);

    // barW relativo al mayor
    const maxTotal = stats[0]?.total || 1;
    stats.forEach(s => s.barW = Math.round((s.total / maxTotal) * 100));
    this.tagStats = stats;

    // Ratio Postobon vs Competencia
    const post = stats.find(s => s.key === 'postobon');
    const comp = stats.find(s => s.key === 'competencia');
    if (post && comp && comp.total > 0) {
      const ratio = post.total / comp.total;
      this.ratioLabel    = `${ratio.toFixed(1)} : 1`;
      this.ratioSubLabel = 'Postobon vs Competencia';
      this.dominanceColor = ratio >= 2 ? '#4ade80' : ratio >= 1 ? '#f59e0b' : '#f87171';
    } else {
      this.ratioLabel = '—';
      this.dominanceColor = '#94a3b8';
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  scoreColor(s: number): string {
    return s >= 0.9 ? '#4ade80' : s >= 0.7 ? '#fb923c' : '#f87171';
  }

  scorePct(s: number): string { return `${Math.round(s * 100)}%`; }

  trackByKey(_: number, s: TagStat): string { return s.key; }
}
